import { useState, useContext, useEffect } from 'react'
import { Typography, Menu, MenuItem, Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { PatternTreeContext } from '../contexts/PatternTreeContext'

const CountText = styled(Typography)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
}))

interface ConditionGroupCountSelectProps {
  groupId: string
  count: number
  totalConditions: number
  readOnly?: boolean
  onEdit?: (isEditing: boolean) => void
}

const ConditionGroupCountSelect = ({
  groupId,
  count,
  totalConditions,
  readOnly = false,
  onEdit,
}: ConditionGroupCountSelectProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { handleChangeConditionGroup } = useContext(PatternTreeContext)

  useEffect(() => {
    if (onEdit) {
      onEdit(Boolean(anchorEl))
    }
  }, [anchorEl, onEdit])

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (readOnly) return
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelectCount = (newCount: number) => {
    if (readOnly) return
    // Directly update the count value in the group
    handleChangeConditionGroup(groupId, 'count', newCount)
    handleClose()
  }

  // Generate menu items based on the total number of conditions
  const menuItems = Array.from({ length: totalConditions }, (_, i) => i + 1)

  return (
    <Box>
      <CountText
        variant="body2"
        color="text.secondary"
        onClick={handleClick}
        aria-controls={readOnly ? undefined : 'count-menu'}
        aria-haspopup={readOnly ? undefined : 'true'}
        sx={{
          cursor: readOnly ? 'default' : 'pointer',
          '&:hover': {
            color: readOnly ? 'text.secondary' : 'primary.main',
            textDecoration: readOnly ? 'none' : 'underline',
          },
        }}
      >
        {count} of {totalConditions} rules required
      </CountText>

      {!readOnly && (
        <Menu
          id="count-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {menuItems.map((num) => (
            <MenuItem key={num} onClick={() => handleSelectCount(num)} selected={num === count}>
              {num} of {totalConditions}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Box>
  )
}

export default ConditionGroupCountSelect
